import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import { useTheme } from "@mui/material/styles";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// GradientLineChart configurations
import configs from "cxfcomponents/Charts/LineCharts/GradientLineChart/configs";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";
import NoDataIcon from "layouts/Flow Designer/components/NoDataIcon";

function GradientLineChart({
  title = "",
  description = "",
  height = "185px",
  chart = {},
  fromHome = false,
}) {
  const chartRef = useRef(null);
  const theme = useTheme();
  const [chartData, setChartData] = useState({});
  const { data, options } = chartData;
  const seriesSummary = (chart.datasets || []).map((dataset) => ({
    label: dataset.label,
    total: (dataset.data || []).reduce((sum, value) => sum + (Number(value) || 0), 0),
    latest: Number(dataset.data?.at(-1)) || 0
  }));
  const latestLabel = chart.labels?.at(-1) || "Latest month";

  if (options && options?.scales?.y) {
    options.scales.y.beginAtZero = true;
  }

  const lineComponent = useMemo(
    () => (
      <SoftBox ref={chartRef} sx={{ height }}>
        <Line data={data} options={options} />
      </SoftBox>
    ),
    [chartData, height]
  );

  useEffect(() => {
    if (!chartRef.current) return;

    const chartDatasets = chart.datasets
      ? chart.datasets.map((dataset) => ({
          ...dataset,
          tension: fromHome ? 0.25 : 0.4,
          pointRadius: fromHome ? 3 : 2,
          pointHoverRadius: fromHome ? 6 : 3,
          pointHitRadius: fromHome ? 12 : 3,
          borderWidth: fromHome ? 2 : 3,
          borderColor: colors[dataset.color]
            ? colors[dataset.color || "dark"].main
            : colors.dark.main,
          fill: !fromHome,
          maxBarThickness: 6,
          backgroundColor: gradientChartLine(
            chartRef.current.children[0],
            colors[dataset.color] ? colors[dataset.color || "dark"].main : colors.dark.main
          ),
        }))
      : [];
    const nextChart = configs(chart.labels || [], chartDatasets.map((dataset, index) => ({
      ...dataset,
      ...(fromHome && chartDatasets.length === 2 ? { yAxisID: index === 0 ? "yCreated" : "yExecuted" } : {})
    })));
    if (fromHome) {
      nextChart.options.plugins.legend = {
        display: true,
        position: "bottom",
        labels: {
          color: theme.palette.mode === "dark" ? "#b7c0cb" : "#475569",
          usePointStyle: true,
          boxWidth: 8,
          padding: 18
        }
      };
      if (chartDatasets.length === 2) {
        delete nextChart.options.scales.y;
        const axisColor = theme.palette.mode === "dark" ? "#b7c0cb" : "#475569";
        nextChart.options.scales.yCreated = {
          type: "linear",
          position: "left",
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: { precision: 0, color: axisColor },
          title: { display: true, text: chartDatasets[0].label, color: axisColor }
        };
        nextChart.options.scales.yExecuted = {
          type: "linear",
          position: "right",
          beginAtZero: true,
          grid: { color: theme.palette.mode === "dark" ? "#27313b" : "#e6ebf0" },
          ticks: { color: axisColor, callback: (value) => Intl.NumberFormat("en-US", { notation: "compact" }).format(value) },
          title: { display: true, text: chartDatasets[1].label, color: axisColor }
        };
      } else {
        nextChart.options.scales.y.ticks.precision = 0;
        nextChart.options.scales.y.grid.display = true;
      }
      nextChart.options.scales.x.title = { display: true, text: "Month", color: theme.palette.mode === "dark" ? "#b7c0cb" : "#475569" };
      nextChart.options.plugins.tooltip = {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`
        }
      };
    }
    setChartData(nextChart);
  }, [chart, height, fromHome, theme.palette.mode]);

  if (
    fromHome &&
    chart.datasets &&
    chart.datasets.every((dataset) => dataset.data.every((data) => data === 0))
  ) {
    return (
      <Card sx={{ height }}>
        <SoftBox p={2}>
          {title || description ? (
            <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
              {title && (
                <SoftBox mb={1}>
                  <SoftTypography variant="h6">{title}</SoftTypography>
                </SoftBox>
              )}
              <SoftBox mb={2}>
                <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
                  {description}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          ) : null}
        </SoftBox>
        <NoDataIcon message="No data to show" />
      </Card>
    );
  }

  const renderChart = () => {
    return (
      <SoftBox p={2}>
        {title || description ? (
          <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
            {title && (
              <SoftBox mb={1}>
                <SoftTypography variant="h6">{title}</SoftTypography>
              </SoftBox>
            )}
            <SoftBox mb={2}>
              <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
                {description}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        ) : null}
        {fromHome && seriesSummary.length > 0 && (
          <SoftBox display="flex" flexWrap="wrap" gap={3} mb={2} px={description ? 1 : 0}>
            {seriesSummary.map((series) => (
              <SoftTypography key={series.label} variant="caption" color="text">
                {series.label} (period): <strong>{series.total.toLocaleString()}</strong>
              </SoftTypography>
            ))}
            <SoftTypography variant="caption" color="text">
              {latestLabel} {seriesSummary.at(-1).label.toLowerCase()}: <strong>{seriesSummary.at(-1).latest.toLocaleString()}</strong>
            </SoftTypography>
          </SoftBox>
        )}
        {lineComponent}
      </SoftBox>
    );
  };

  return title || description ? (
    <Card sx={{ boxShadow: "none" }}>{renderChart()}</Card>
  ) : null /*renderChart()*/;
}

// Typechecking props for the GradientLineChart
GradientLineChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  fromHome: PropTypes.bool,
};

export default GradientLineChart;
